// VENDORED VERBATIM from the Designer Portal repo (4myaibot/designer-portal)
//   path:   apps/shared/price-section.js
//   branch: codex/monorepo-preview @ commit 2b6497b3
//   vendored 2026-08-09 for Phase 4e (ADR 0058), per
//   docs/PRICING_AND_INTERIORS_PLAN.md §3: "the Designer Portal Enhancements
//   room-sketch tool vendors this exact file". DO NOT MODIFY here — fix in
//   the portal repo and re-vendor (only this provenance header is local).

// priceSection() — Phase 2 of docs/PRICING_AND_INTERIORS_PLAN.md.
//
// Pure, dependency-free pricing over the canonical CV section catalog
// (data/cv-pricing/catalog.json "sections" array). Shared contract: this repo's
// ballpark config generator consumes it directly, and the Designer Portal
// Enhancements room-sketch tool vendors this exact file. No DOM, no I/O.
//
// Result shape (never throws on unpriceable input):
//   { ok: true, price, materialPrice, nonMaterialPrice, perPricing,
//     basis: "exact" | "interpolated" | "extrapolated" | "fallback",
//     anchors: [source codes used], note?: string }
//   { ok: false, reason: string }
//
// Rules (plan §3):
// - Exact (class, height, depth, width, config) match wins.
// - Width interpolation needs BOTH the 24" and 36" anchors at matching
//   class/height/depth/config; linear per-inch between them, linear on the
//   same slope outside them (basis "extrapolated", width clamped to
//   MIN_EXTRAPOLATION_WIDTH..MAX_EXTRAPOLATION_WIDTH).
// - Panels (config P), connecting shelves (CS), wall beds (WB), and any
//   combo without a 24/36 anchor pair are exact-only.
// - Missing combos fall back nearest-depth-then-nearest-height (ties take the
//   smaller size), re-entering the width rules at the substitute dimensions;
//   basis "fallback" with a note so UIs can badge it. NO fallback across
//   configs or classes — an unknown configuration is unpriceable, never
//   approximated (the no-guessing rule).

const INTERPOLATION_ANCHOR_WIDTHS = Object.freeze([24, 36]);
const MIN_EXTRAPOLATION_WIDTH = 12;
const MAX_EXTRAPOLATION_WIDTH = 48;

function keyOf(classId, heightIn, depthIn, widthIn, config) {
  const part = (value) => (value === null || value === undefined ? "NA" : String(value));
  return [classId, part(heightIn), part(depthIn), part(widthIn), config].join("|");
}

export function indexCatalog(sections) {
  const byKey = new Map();
  const byGroup = new Map();
  for (const row of sections) {
    byKey.set(keyOf(row.class, row.heightIn, row.depthIn, row.widthIn, row.config), row);
    const groupKey = keyOf(row.class, row.heightIn, row.depthIn, null, row.config);
    if (!byGroup.has(groupKey)) byGroup.set(groupKey, []);
    byGroup.get(groupKey).push(row);
  }
  return { sections, byKey, byGroup };
}

function exactResult(row, basis, anchors, note) {
  const result = {
    ok: true,
    price: row.price,
    materialPrice: row.materialPrice,
    nonMaterialPrice: row.nonMaterialPrice,
    perPricing: row.perPricing,
    basis,
    anchors
  };
  if (note) result.note = note;
  return result;
}

function round2(value) {
  return Math.round(value * 100) / 100;
}

function interpolatedResult(a24, a36, widthIn, basisNote) {
  const slopeMaterial = (a36.materialPrice - a24.materialPrice) / 12;
  const slopeNonMaterial = (a36.nonMaterialPrice - a24.nonMaterialPrice) / 12;
  const materialPrice = round2(a24.materialPrice + slopeMaterial * (widthIn - 24));
  const nonMaterialPrice = round2(a24.nonMaterialPrice + slopeNonMaterial * (widthIn - 24));
  if (materialPrice + nonMaterialPrice <= 0) {
    return { ok: false, reason: `width ${widthIn}" extrapolates to a non-positive price` };
  }
  const inside = widthIn >= 24 && widthIn <= 36;
  const result = {
    ok: true,
    price: round2(materialPrice + nonMaterialPrice),
    materialPrice,
    nonMaterialPrice,
    perPricing: a24.perPricing,
    basis: inside ? "interpolated" : "extrapolated",
    anchors: [a24.source, a36.source]
  };
  if (basisNote) result.note = basisNote;
  return result;
}

function widthRuleResult(index, classId, heightIn, depthIn, widthIn, config, fallbackNote) {
  const exact = index.byKey.get(keyOf(classId, heightIn, depthIn, widthIn, config));
  if (exact) {
    return exactResult(exact, fallbackNote ? "fallback" : "exact", [exact.source], fallbackNote);
  }
  if (typeof widthIn !== "number") return null;
  const a24 = index.byKey.get(keyOf(classId, heightIn, depthIn, INTERPOLATION_ANCHOR_WIDTHS[0], config));
  const a36 = index.byKey.get(keyOf(classId, heightIn, depthIn, INTERPOLATION_ANCHOR_WIDTHS[1], config));
  if (!a24 || !a36) return null;
  if (widthIn < MIN_EXTRAPOLATION_WIDTH || widthIn > MAX_EXTRAPOLATION_WIDTH) {
    return {
      ok: false,
      reason: `width ${widthIn}" is outside the supported ${MIN_EXTRAPOLATION_WIDTH}"-${MAX_EXTRAPOLATION_WIDTH}" range`
    };
  }
  const result = interpolatedResult(a24, a36, widthIn, fallbackNote);
  if (result.ok && fallbackNote) result.basis = "fallback";
  return result;
}

function nearestValues(available, target) {
  return [...available].sort((a, b) => {
    const diff = Math.abs(a - target) - Math.abs(b - target);
    return diff !== 0 ? diff : a - b;
  });
}

export function priceSection(catalogIndex, query) {
  const index = Array.isArray(catalogIndex) ? indexCatalog(catalogIndex) : catalogIndex;
  const { class: classId, heightIn = null, depthIn = null, widthIn = null, config } = query || {};
  if (!classId || !config) return { ok: false, reason: "class and config are required" };

  // Exact-only families: panels, connecting shelves, wall beds.
  if (config === "P" || classId === "CS" || classId === "WB") {
    const exact = index.byKey.get(keyOf(classId, heightIn, depthIn, widthIn, config));
    return exact
      ? exactResult(exact, "exact", [exact.source])
      : { ok: false, reason: `${classId} ${config} is exact-only and has no row at these dimensions` };
  }

  const direct = widthRuleResult(index, classId, heightIn, depthIn, widthIn, config);
  if (direct) return direct;

  // Nearest-size fallback: same class + config only. Depth first, then height.
  const candidates = index.sections.filter((row) => row.class === classId && row.config === config);
  if (!candidates.length) {
    return { ok: false, reason: `no ${classId} rows exist for configuration ${config} (needs pricing)` };
  }
  const depths = new Set(
    candidates.filter((row) => row.heightIn === heightIn).map((row) => row.depthIn)
  );
  for (const depth of nearestValues(depths, depthIn ?? 0)) {
    const result = widthRuleResult(
      index, classId, heightIn, depth, widthIn, config,
      `no ${depthIn}" depth priced; used nearest depth ${depth}"`
    );
    if (result) return result;
  }
  const heights = new Set(candidates.map((row) => row.heightIn));
  for (const height of nearestValues(heights, heightIn ?? 0)) {
    const heightDepths = new Set(
      candidates.filter((row) => row.heightIn === height).map((row) => row.depthIn)
    );
    for (const depth of nearestValues(heightDepths, depthIn ?? 0)) {
      const result = widthRuleResult(
        index, classId, height, depth, widthIn, config,
        `no ${heightIn}"h x ${depthIn}"d priced; used nearest ${height}"h x ${depth}"d`
      );
      if (result) return result;
    }
  }
  return {
    ok: false,
    reason: `no priceable ${classId} ${config} row reachable from ${heightIn}"h x ${depthIn}"d x ${widthIn}"w`
  };
}
